import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ThumbsDown, Meh, ThumbsUp, Heart, ExternalLink } from 'lucide-react';

const ratings = [
  { key: 'bad', icon: ThumbsDown, label: 'Bad', color: 'text-red-400 border-red-500/30 hover:bg-red-500/10' },
  { key: 'okay', icon: Meh, label: 'Okay', color: 'text-amber-400 border-amber-500/30 hover:bg-amber-500/10' },
  { key: 'good', icon: ThumbsUp, label: 'Good', color: 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10' },
  { key: 'excellent', icon: Heart, label: 'Excellent', color: 'text-gold border-gold/30 hover:bg-gold/10' },
];

export default function FeedbackPrompt({ orderId, onClose }) {
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const submit = async () => {
    if (!selected) { toast.error('Please select a rating'); return; }
    try {
      await api.post('/feedback', { order_id: orderId, rating: selected, comment });
      setSubmitted(true);
      if (selected === 'good' || selected === 'excellent') {
        setShowReview(true);
      } else {
        toast.success('Thank you for your feedback');
        setTimeout(onClose, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  if (showReview) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="review-prompt-overlay">
        <div className="bg-bg-secondary border border-white/10 rounded-sm p-6 max-w-sm w-full text-center animate-fadeIn">
          <Heart className="w-10 h-10 text-gold mx-auto mb-3" />
          <h2 className="text-xl font-serif text-white mb-2">Thank You!</h2>
          <p className="text-zinc-400 font-sans text-sm mb-4">We're thrilled you enjoyed your experience at Majnu.</p>
          <p className="text-gold font-sans text-sm mb-4">Leave a Google review and get <span className="font-bold">5% off</span> your next visit!</p>
          <a href="https://g.page/review/majnu" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-black font-bold rounded-sm font-sans text-sm hover:bg-gold-light transition-all mb-3" data-testid="google-review-link">
            <ExternalLink className="w-4 h-4" /> Write a Google Review
          </a>
          <p className="text-zinc-500 text-xs font-sans mb-4">Show your review to staff to claim your discount</p>
          <button onClick={onClose} className="text-zinc-500 text-xs font-sans hover:text-zinc-400" data-testid="review-dismiss-button">Maybe later</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="feedback-prompt-overlay">
      <div className="bg-bg-secondary border border-white/10 rounded-sm p-6 max-w-sm w-full text-center animate-fadeIn">
        <h2 className="text-xl font-serif text-white mb-2">How was your experience?</h2>
        <p className="text-zinc-400 font-sans text-sm mb-6">Your feedback helps us improve</p>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {ratings.map(r => {
            const Icon = r.icon;
            return (
              <button
                key={r.key}
                onClick={() => setSelected(r.key)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-sm border transition-all ${selected === r.key ? `${r.color} bg-opacity-20` : 'border-white/10 text-zinc-500'}`}
                data-testid={`feedback-rating-${r.key}`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs font-sans">{r.label}</span>
              </button>
            );
          })}
        </div>

        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          className="w-full p-3 bg-transparent border border-white/10 rounded-sm text-white font-sans text-sm placeholder:text-zinc-600 focus:border-gold focus:outline-none mb-4 resize-none h-20"
          placeholder="Tell us more (optional)..."
          data-testid="feedback-comment-input"
        />

        <Button onClick={submit} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-feedback-button">
          Submit Feedback
        </Button>
        <button onClick={onClose} className="mt-3 text-zinc-500 text-xs font-sans hover:text-zinc-400 block mx-auto" data-testid="feedback-skip-button">Skip</button>
      </div>
    </div>
  );
}
